import { Client } from "@/Common/Api/Client";
import { ValidatePinResponse } from "@/Common/Api/Pin/Dto/ValidatePinResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface ISignaturePinControlProps {
    onSuccessFunction: (pin: string) => void;
    onRejectFunction: () => void;
    onEscapeDelegate?: () => void;
    dataCy?: string;

    /**
     * Tells if the signature is loading or not
     */
    onLoadingStateChange?: (isLoading: boolean, callback?: () => void) => void;
    pinLength?: number;
    pinWarningRenderRight?: boolean;
    disableAutoFocus?: boolean;
    disableOnSuccess?: boolean;
    disableControl?: boolean;
}

class SignaturePinControlState {
    public inProgress: boolean;
    public pinCheckPass: boolean;
    public pinCheckDone: boolean;
    public blurredPin: string;
    public pinMembers: string[];
    public textboxSelectionStart: number;
    public textboxSelectionEnd: number;
    public requiredPinLength: number;
}

class DefaultSignaturePinControlState extends SignaturePinControlState {
    constructor(requiredPinLength: number) {
        super();
        this.inProgress = false;
        this.pinCheckPass = false;
        this.pinCheckDone = false;
        this.blurredPin = "";
        this.pinMembers = [];
        this.textboxSelectionStart = 0;
        this.textboxSelectionEnd = 0;
        this.requiredPinLength = requiredPinLength || 4;
    }
}

class SignaturePinControl extends BaseReactComponent<ISignaturePinControlProps, SignaturePinControlState> {
    private inputDOMId: string = "__pinfcsbx001";
    private blurringChar = "•";
    private cssStyle: React.CSSProperties = { fontFamily: "Arial" }; // we are forcing Arial here, as the encodingChar is guaranteed to be the visual symbol we want.
    private inputElement: any;

    constructor(props: ISignaturePinControlProps) {
        super(props);
        this.state = new DefaultSignaturePinControlState(this.props.pinLength);
    }

    public render(): JSX.Element {
        let hasPinWarning = this.state.pinCheckDone && !this.state.pinCheckPass;

        return (
            <div>
                {hasPinWarning && !this.props.pinWarningRenderRight &&
                    <small className="red-text" style={{ "paddingRight": "5px" }}>{cacheStore.GetString("InvalidPIN")}</small>
                }
                <label className="form-label signature-pin-control">
                    <input
                        id={this.inputDOMId}
                        className="form-control field"
                        maxLength={this.state.requiredPinLength}
                        minLength={this.state.requiredPinLength}
                        disabled={(this.props.disableOnSuccess && this.state.pinCheckPass) || this.props.disableControl}
                        data-cy={this.props.dataCy}

                        type="text"
                        //  As of Jan 2. 2020 Our requirement is to always have to enter the sensitive passwords/pins, and not have any annoying popups
                        //  such as autocompletion, offering to store passwords, offering stronger password...
                        //  The reason we are using text instead of password (as common sense would dicate) is
                        //  because Chrome will simply not leave this textfield alone if type=passwd. Doesn't matter what you set autoComplete to.
                        //  if type=text and autoComplete is set to anything that is not part of the browser standards, chrome will leave this field alone.

                        onKeyDown={this.onPinChange.bind(this)}
                        value={this.state.blurredPin}
                        autoComplete="security-sensitive-pin" // https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164
                        placeholder={cacheStore.GetString("PINLabel")}
                        style={this.cssStyle}
                        ref={element => this.setInputElementRef(element)}

                        onChange={() => null} // an input isn't supposed to define a value without an onChange event; this component is an exception because it's handled in onKeyDown.
                    />
                </label>
                {hasPinWarning && this.props.pinWarningRenderRight &&
                    <small className="red-text" style={{ "paddingLeft": "5px" }}>{cacheStore.GetString("InvalidPIN")}</small>
                }
            </div>
        );
    }

    public componentDidMount(): void {
        !this.props.disableAutoFocus && this.inputElement.focus();
    }

    private onPinChange(e: React.KeyboardEvent): void {
        e.preventDefault();//  This line prevents the typed character to be displayed on screen, so be very very very carefull if you want to remove it.
        //  This is because we set the input field type on top to "text"
        e.persist();

        this.mutateState(
            state => {
                let didChange = this.handlePressedKey(state, e);
                if (didChange) {
                    this.setBlurredPin(state);
                    this.tryValidatePinControlState(state);
                }
            },
            () => this.setCursor(this.state)
        );
    }

    private handlePressedKey(targetState: SignaturePinControlState, e: React.KeyboardEvent): boolean {
        const pinInputElement = (document.getElementById(this.inputDOMId) as HTMLInputElement);
        let pinMembers = targetState.pinMembers.join("").trim();

        switch (e.key) {
            case "Backspace":
                this.handleBackspace(targetState, pinInputElement);
                break;
            case "ArrowLeft":
                this.handleArrowLeft(targetState, e);
                break;
            case "ArrowRight":
                this.handleArrowRight(targetState, e);
                break;
            case "Delete":
                this.handleDelete(targetState, pinInputElement);
                break;
            case "Home":
                this.handleHome(targetState);
                break;
            case "End":
                this.handleEnd(targetState);
                break;
            default:
                this.tryStoreTypedChar(targetState, e);
        }
        return pinMembers != targetState.pinMembers.join("").trim(); // hasChanged?
    }

    private setCursor(targetState: SignaturePinControlState): void {
        this.inputElement.selectionStart = targetState.textboxSelectionStart;
        this.inputElement.selectionEnd = targetState.textboxSelectionEnd;
    }

    private setInputElementRef(element: HTMLInputElement): void {
        this.inputElement = element;
    }

    private getPinFromState(targetState: SignaturePinControlState): string {
        return targetState.pinMembers.join("").trim();
    }

    private setBlurredPin(targetState: SignaturePinControlState): SignaturePinControl {
        targetState.blurredPin = Array(targetState.pinMembers.length + 1).join(this.blurringChar);
        return this;
    }

    private async tryValidatePinControlState(targetState: SignaturePinControlState): Promise<SignaturePinControl> {
        var pin = this.getPinFromState(targetState);

        if (pin.length === targetState.requiredPinLength) {
            if (pin.length == targetState.requiredPinLength && pin.match(/^\d+$/)) {
                let self = this;

                const callback = async (): Promise<void> => {
                    try {
                        const response = await Client.postAsync<ValidatePinResponse>(
                            ServerOperations.Server.Configuration_Validate_PIN,
                            { UserId: sessionStore.userId, PIN: pin }
                        );

                        self.setStateForPinCheck(response.data.Valid);
                    }
                    catch (error: any) {
                        console.error(error);
                    }
                    finally {
                        this.props.onLoadingStateChange?.(false);
                    }
                };

                if (this.props.onLoadingStateChange) {
                    this.props.onLoadingStateChange(true, callback);
                }
                else {
                    await callback();
                }
            }
        }
        else {
            targetState.pinCheckDone = false;
        }
        return this;
    }

    private setStateForPinCheck(success: boolean): void {
        this.mutateState(s => {
            s.pinCheckPass = success;
            s.pinCheckDone = true;
        });

        if (success === true) {
            this.props.onSuccessFunction?.(this.state.pinMembers.join(""));
        }
        else if (this.props.onRejectFunction != null && success === false) {
            this.props.onRejectFunction();
        }
    }

    private tryStoreTypedChar(targetState: SignaturePinControlState, e: React.KeyboardEvent): void {
        if (e.key.match(/^\d$/)) {
            if (this.getStateHasSelection(targetState))
                this.deleteTextboxSelection(targetState);

            if (targetState.pinMembers.length < this.state.requiredPinLength) {
                targetState.pinMembers.splice(targetState.textboxSelectionStart, 0, e.key);
                this.tryMoveCursorRight(targetState);
            }
        }
    }

    private handleArrowLeft(targetState: SignaturePinControlState, e: React.KeyboardEvent): void {
        if (e.shiftKey && e.ctrlKey) {
            targetState.textboxSelectionStart = 0;
            return;
        }

        if (e.shiftKey) {
            if (targetState.textboxSelectionStart > 0) {
                targetState.textboxSelectionStart--;
                return;
            }
        }

        if (e.ctrlKey) {
            targetState.textboxSelectionStart = 0;
            targetState.textboxSelectionEnd = 0;
            return;
        }

        this.tryMoveCursorLeft(targetState);
    }

    private handleArrowRight(targetState: SignaturePinControlState, e: React.KeyboardEvent): void {
        if (e.shiftKey && e.ctrlKey) {
            targetState.textboxSelectionEnd = targetState.requiredPinLength;
            return;
        }

        if (e.shiftKey) {
            if (targetState.textboxSelectionEnd < targetState.requiredPinLength) {
                targetState.textboxSelectionEnd++;
                return;
            }
        }

        if (e.ctrlKey) {
            targetState.textboxSelectionStart = targetState.pinMembers.length;
            targetState.textboxSelectionEnd = targetState.pinMembers.length;
            return;
        }

        this.tryMoveCursorRight(targetState);
    }

    private tryMoveCursorLeft(targetState: SignaturePinControlState): void {
        if (targetState.textboxSelectionStart > 0 && !this.getStateHasSelection(targetState))
            targetState.textboxSelectionStart--;

        targetState.textboxSelectionEnd = targetState.textboxSelectionStart;
    }

    private tryMoveCursorRight(targetState: SignaturePinControlState): void {
        if (targetState.textboxSelectionStart < targetState.requiredPinLength && !this.getStateHasSelection(targetState))
            targetState.textboxSelectionEnd++;

        targetState.textboxSelectionStart = targetState.textboxSelectionEnd;
    }

    private handleDelete(targetState: SignaturePinControlState, pinInputElement: HTMLInputElement): void {
        if (pinInputElement.selectionStart < pinInputElement.selectionEnd) {
            this.handleMouseSelectBackSpaceAndDelete(targetState, pinInputElement);
        }
        else {
            this.getStateHasSelection(targetState)
                ? this.deleteTextboxSelection(targetState)
                : this.tryDeleteSingleCharNext(targetState);
        }
    }

    private handleBackspace(targetState: SignaturePinControlState, pinInputElement: HTMLInputElement): void {
        if (pinInputElement.selectionStart < pinInputElement.selectionEnd) {
            this.handleMouseSelectBackSpaceAndDelete(targetState, pinInputElement);
        }
        else {
            this.getStateHasSelection(targetState)
                ? this.deleteTextboxSelection(targetState)
                : this.tryDeleteSingleCharPrevious(targetState);
        }
    }

    private handleHome(targetState: SignaturePinControlState): void {
        targetState.textboxSelectionStart = targetState.textboxSelectionEnd = 0;
    }

    private handleEnd(targetState: SignaturePinControlState): void {
        targetState.textboxSelectionStart = targetState.textboxSelectionEnd = this.getPinFromState(targetState).length;
    }

    private tryDeleteSingleCharNext(targetState: SignaturePinControlState): void {
        targetState.pinMembers.splice(targetState.textboxSelectionStart, 1);
    }

    private tryDeleteSingleCharPrevious(targetState: SignaturePinControlState): void {
        if (targetState.textboxSelectionStart > 0) {
            targetState.textboxSelectionStart--;
            targetState.textboxSelectionEnd = targetState.textboxSelectionStart;
            targetState.pinMembers.splice(targetState.textboxSelectionStart, 1);
        }
    }

    private deleteTextboxSelection(targetState: SignaturePinControlState): void {
        targetState.pinMembers.splice(targetState.textboxSelectionStart, this.getStateSelectionLength(targetState));
        targetState.textboxSelectionEnd = targetState.textboxSelectionStart;
    }

    private getStateSelectionLength(targetState: SignaturePinControlState): number {
        return targetState.textboxSelectionEnd - targetState.textboxSelectionStart;
    }

    private getStateHasSelection(targetState: SignaturePinControlState): boolean {
        return targetState.textboxSelectionStart < targetState.textboxSelectionEnd;
    }

    private handleMouseSelectBackSpaceAndDelete(targetState: SignaturePinControlState, pinInputElement: HTMLInputElement): void {
        targetState.pinMembers.splice(pinInputElement.selectionStart, pinInputElement.selectionEnd);
        targetState.textboxSelectionStart = 0;
        targetState.textboxSelectionEnd = 0;
    }
}

export { SignaturePinControl };
